import Form from 'react-bootstrap/Form';
import { useState } from 'react';
import './style.css';
import { lang } from '../../../utils/langs';
import SubaccountSearchDropdown from '../../molecules/SubaccountSearchDropdown/view';
import Cuentas from '../cuentas/view';
import ButtonLoading from '../../atoms/buttonLoading/view';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';
import ButtonConfirm from '../../atoms/ButtonConfirm/view';

const TAX_TYPES = {
    "1": "IVA",
    "2": "IC",
    "3": "ICA",
    "4": "INC",
    "5": "ReteIVA",
    "6": "ReteRenta",
    "7": "ReteICA",
    "8": "FtoHorticultura",
    "9": "Timbre",
    "10": "INC Bolsas",
    "11": "INCarbono",
    "12": "INCombustibles",
    "13": "Sobretasa Combustibles",
    "14": "Sordicom",
    "16": "IC Porcentual",
    "17": "IC Datos",
    "18": "IVA e INC",
    "19": "No aplica",
    "20": "ICL",
    "21": "INPP",
    "22": "IBUA",
    "23": "ICUI"
};

const ACCOUNT_TYPES = [
    { account_type: "compra" },
    { account_type: "venta" },
    { account_type: "devolucion_venta" },
    { account_type: "devolucion_compra" },
    { account_type: "gasto" },
];

function TaxForm({
    initialData = {},
    onSubmit,
    loadingId,
    title,
    submitLabel,
    modalId,
    showDelete = false,
    onDelete,
}) {
    const [tax, setTax] = useState({
        name: '',
        tax_type: '',
        accounts: ACCOUNT_TYPES.map(a => ({ account_type: a.account_type, account_id: '' })),
        ...initialData,
        // Convert value from decimal (0.19) → display as percentage (19)
        value: initialData?.value !== undefined && initialData?.value !== ''
            ? parseFloat(initialData.value) * 100
            : '',
    });

    const [errors, setErrors] = useState({ name: false, value: false });

    function handleChange(e) {
        e.preventDefault();
        const { id, value } = e.target;
        const key = id.replace('tax-', '');
        setTax(prev => {
            const next = { ...prev };
            if (key === 'value') {
                const n = parseFloat(value);
                next[key] = isNaN(n) ? value : n;
            } else {
                next[key] = value;
            }
            return next;
        });
        if (key === 'name') setErrors(e => ({ ...e, name: value.trim() === '' }));
        if (key === 'value') {
            const n = parseFloat(value);
            setErrors(e => ({ ...e, value: isNaN(n) || n < 0 || n > 100 }));
        }
    }

    function handleAccountSelect(index, value) {
        setTax(prev => {
            const accounts = [...prev.accounts];
            accounts[index] = {
                account_id: value.accounts_id,
                sub_account_id: value.id,
                account_type: ACCOUNT_TYPES[index].account_type,
            };
            return { ...prev, accounts };
        });
    }

    function handleSubmit() {
        if (!tax.name.trim() || errors.value) return;
        const payload = {
            ...tax,
            value: parseFloat(tax.value) / 100,
            tax_type: parseInt(tax.tax_type),
            accounts: tax.accounts.filter(a => 'sub_account_id' in a),
        };
        onSubmit(payload);
    }

    return (
        <div className="tax-form-container">
            <h2 className="tax-form-title">{title}</h2>

            <Form action="javascript:void(0);" style={{ width: '100%', display: 'block' }}>

                {/* ── Sección 1: Información Básica ── */}
                <div className="tax-form-section-split">
                    <div className="tax-form-sidebar">
                        <h3 className="tax-form-section-title">Información Básica</h3>
                        <p className="tax-form-section-desc">Nombre, valor porcentual y tipología de tributación del impuesto.</p>
                    </div>

                    <div className="tax-form-right">
                        <div className="tax-form-fields-row">
                            <div className="tax-form-field-col">
                                <Form.Group controlId="tax-name" className="tax-form-group">
                                    <Form.Label>Nombre</Form.Label>
                                    <Form.Control
                                        className="tax-form-input"
                                        type="text"
                                        placeholder="Ej: IVA 19%"
                                        onChange={handleChange}
                                        value={tax.name}
                                        isInvalid={errors.name}
                                    />
                                    {errors.name && <div className="tax-form-error">El nombre es obligatorio.</div>}
                                </Form.Group>
                            </div>
                            <div className="tax-form-field-col">
                                <Form.Group controlId="tax-value" className="tax-form-group">
                                    <Form.Label>Valor (%)</Form.Label>
                                    <Form.Control
                                        className="tax-form-input"
                                        type="number"
                                        step="0.01"
                                        placeholder="ej. 19"
                                        onChange={handleChange}
                                        value={tax.value}
                                        isInvalid={errors.value}
                                    />
                                    {errors.value && <div className="tax-form-error">Número entre 0 y 100.</div>}
                                </Form.Group>
                            </div>
                        </div>

                        <div className="tax-form-fields-row">
                            <div className="tax-form-field-col">
                                <Form.Group controlId="tax-tax_type" className="tax-form-group">
                                    <Form.Label>Tipo de Impuesto</Form.Label>
                                    <Form.Select
                                        className="tax-form-select"
                                        value={tax.tax_type}
                                        onChange={handleChange}
                                    >
                                        <option value="">Seleccionar tipo...</option>
                                        {Object.keys(TAX_TYPES).map(key => (
                                            <option key={key} value={key}>{key} - {TAX_TYPES[key]}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>
                        </div>
                    </div>
                </div>

                <hr className="tax-form-separator" />

                {/* ── Sección 2: Cuentas Contables ── */}
                <div className="tax-form-section-split">
                    <div className="tax-form-sidebar">
                        <h3 className="tax-form-section-title">Cuentas de Impuesto</h3>
                        <p className="tax-form-section-desc">Vincula las subcuentas auxiliares para compra, venta y devoluciones.</p>
                    </div>

                    <div className="tax-form-right">
                        <div className="tax-form-accounts-header">
                            <h4 className="tax-form-accounts-title">Configuración Contable</h4>
                            <Cuentas id={modalId} />
                        </div>

                        <div className="tax-form-accounts-grid">
                            {ACCOUNT_TYPES.map((account, index) => (
                                <div className="tax-form-field-col" key={index}>
                                    <Form.Group className="tax-form-group">
                                        <Form.Label>{lang('es', account.account_type)}</Form.Label>
                                        <SubaccountSearchDropdown
                                            selected={
                                                initialData?.accounts?.find(a => a.account_type === account.account_type) ||
                                                initialData?.tax_accounts?.find(a => a.account_type === account.account_type)
                                            }
                                            onSelected={(value) => handleAccountSelect(index, value)}
                                        />
                                    </Form.Group>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

            </Form>
            <div className="tax-form-footer">
                {showDelete && onDelete && (
                    <ButtonConfirm
                        eventKey="delete-tax"
                        onAcept={onDelete}
                        msg="¿Estás seguro de eliminar este impuesto?"
                        title="Eliminar"
                        className="btn-secondary-danger"
                    />
                )}
                <ButtonPrimary
                    onClick={handleSubmit}
                    id={loadingId}
                    title={submitLabel}
                />
            </div>
        </div>
    );
}

export default TaxForm;
